import React, { useState } from 'react';
import {
  Select,
  TokenGroup,
  Modal,
  Box,
  Button,
  SpaceBetween,
  Checkbox,
  SegmentedControl,
  ColumnLayout,
  FormField,
  Multiselect,
} from '@cloudscape-design/components';
import type { MultiselectProps } from '@cloudscape-design/components';

interface PeriodFilterProps {
  selectedPeriods: string[];
  onPeriodsChange: (periods: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

enum Control {
  RELATIVE = 'relative',
  CUSTOM = 'custom',
}

const PRIOR_MONTH_OPTIONS = [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: '9', value: '9' },
  { label: '10', value: '10' },
  { label: '11', value: '11' },
];

const CUSTOM_YEARS: MultiselectProps.Options = [
  { label: 'Current Year', value: 'current' },
  { label: 'Prior Year', value: 'prior' },
  { label: '2 Years Ago', value: 'two_years' },
];

const CUSTOM_MONTHS: MultiselectProps.Options = [
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' },
];

export const PeriodFilter: React.FC<PeriodFilterProps> = ({
  selectedPeriods,
  onPeriodsChange,
  isLoading = false,
  disabled = false,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [control, setControl] = useState<Control>(Control.RELATIVE);
  const [relativePeriods, setRelativePeriods] = useState<string[]>([]);
  const [priorMonths, setPriorMonths] = useState('1');
  const [priorMonthsYearAgo, setPriorMonthsYearAgo] = useState('1');
  const [customYears, setCustomYears] = useState<MultiselectProps.Options>([]);
  const [customMonths, setCustomMonths] = useState<MultiselectProps.Options>([]);

  const generateRelativePeriods = () => {
    const periods: string[] = [];
    relativePeriods.forEach((period) => {
      if (period === 'Prior Month(s)') {
        periods.push(`Prior ${priorMonths} Month(s)`);
      } else if (period === 'Prior Month(s) A Year Ago') {
        periods.push(`Prior ${priorMonthsYearAgo} Month(s) A Year Ago`);
      } else {
        periods.push(period);
      }
    });
    return periods;
  };

  const generateCustomPeriods = () => {
    const periods: string[] = [];
    customYears.forEach((year) => {
      customMonths.forEach((month) => {
        periods.push(`${year.label} ${month.label}`);
      });
    });
    return periods;
  };

  const getCurrentPeriods = () => {
    return control === Control.RELATIVE ? generateRelativePeriods() : generateCustomPeriods();
  };

  const handleApply = () => {
    const periods = getCurrentPeriods();
    onPeriodsChange(periods);
    setModalVisible(false);
  };

  const handleCancel = () => {
    setModalVisible(false);
  };

  const handleClear = () => {
    onPeriodsChange([]);
  };

  const handleRelativePeriodChange = (period: string, checked: boolean) => {
    if (checked) {
      setRelativePeriods([...relativePeriods, period]);
    } else {
      setRelativePeriods(relativePeriods.filter((p) => p !== period));
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={() => setModalVisible(true)}
        onClick={() => setModalVisible(true)}
      >
        <Select
          options={[]}
          placeholder="Select Period Name"
          selectedOption={null}
          disabled={isLoading || disabled}
        />
      </div>

      {selectedPeriods.length > 0 && (
        <TokenGroup onDismiss={handleClear} items={[{ label: selectedPeriods.join(', ') }]} />
      )}

      {modalVisible && (
        <Modal
          visible={modalVisible}
          onDismiss={handleCancel}
          header="Period"
          footer={
            <Box float="right">
              <Button
                variant="primary"
                onClick={handleApply}
                disabled={getCurrentPeriods().length === 0}
              >
                Apply
              </Button>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <SegmentedControl
              selectedId={control}
              onChange={({ detail }) => setControl(detail.selectedId as Control)}
              options={[
                { text: 'Relative', id: Control.RELATIVE },
                { text: 'Custom', id: Control.CUSTOM },
              ]}
            />

            {control === Control.RELATIVE && (
              <SpaceBetween size="s">
                <Checkbox
                  checked={relativePeriods.includes('Current Month')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Current Month', detail.checked)
                  }
                >
                  Current Month
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Current Month (A Year Ago)')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Current Month (A Year Ago)', detail.checked)
                  }
                >
                  Current Month (A Year Ago)
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Prior Month(s)')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Prior Month(s)', detail.checked)
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Prior</span>
                    <div style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                      <Select
                        selectedOption={{ label: priorMonths, value: priorMonths }}
                        options={PRIOR_MONTH_OPTIONS}
                        onChange={({ detail }) =>
                          setPriorMonths(detail.selectedOption.value || '1')
                        }
                      />
                    </div>
                    <span>Month(s)</span>
                  </div>
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Prior Month(s) A Year Ago')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Prior Month(s) A Year Ago', detail.checked)
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Prior</span>
                    <div style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                      <Select
                        selectedOption={{ label: priorMonthsYearAgo, value: priorMonthsYearAgo }}
                        options={PRIOR_MONTH_OPTIONS}
                        onChange={({ detail }) =>
                          setPriorMonthsYearAgo(detail.selectedOption.value || '1')
                        }
                      />
                    </div>
                    <span>Month(s) A Year Ago</span>
                  </div>
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Next Month')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Next Month', detail.checked)
                  }
                >
                  Next Month
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Next Month (A Year Ago)')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Next Month (A Year Ago)', detail.checked)
                  }
                >
                  Next Month (A Year Ago)
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Current Year')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Current Year', detail.checked)
                  }
                >
                  Current Year
                </Checkbox>

                <Checkbox
                  checked={relativePeriods.includes('Current Quarter')}
                  onChange={({ detail }) =>
                    handleRelativePeriodChange('Current Quarter', detail.checked)
                  }
                >
                  Current Quarter
                </Checkbox>
              </SpaceBetween>
            )}

            {control === Control.CUSTOM && (
              <ColumnLayout columns={2}>
                <FormField label="Year">
                  <Multiselect
                    filteringType="auto"
                    placeholder="Select Year"
                    options={CUSTOM_YEARS}
                    selectedOptions={customYears}
                    onChange={({ detail }) => setCustomYears([...detail.selectedOptions])}
                    keepOpen={true}
                  />
                </FormField>
                <FormField label="Month">
                  <Multiselect
                    filteringType="auto"
                    placeholder="Select Month"
                    options={CUSTOM_MONTHS}
                    selectedOptions={customMonths}
                    onChange={({ detail }) => setCustomMonths([...detail.selectedOptions])}
                    keepOpen={true}
                  />
                </FormField>
              </ColumnLayout>
            )}
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};
